import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Geolocation } from "@capacitor/geolocation";
import { FloatingInput, Spinner } from "../../Components/Common";
import * as TaskService from "../../Services/TaskService";
import { HOME } from "../../routes";
import { MESSAGES } from "./constants";

const libraries = ["places"];

const AddTask = () => {
  const containerStyle = { width: "100%", height: "300px" };
  const navigate = useNavigate();
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const dayId = searchParams.get("dayId");
  const [isMapReady, setIsMapReady] = useState(false);
  const [isGeoReady, setIsGeoReady] = useState(false);

  const initialAttendanceValue = {
    site_id: "",
    date: new Date().toISOString().split("T")[0],
    start_time: new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const getGeoLocation = async () => {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000, // optional: set timeout
        maximumAge: 0, // avoid cached location
      });
      const coords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      setLocation(coords);
      setIsGeoReady(true);
    } catch (error) {
      toast.error(MESSAGES.LOCATION_DENIED_ERROR);
    }
  };

  // To be done later
  const handleSubmit = async (values) => {
    if (submitting) return;
    setSubmitting(true);

    const formData = new FormData();
    formData.append("task_title", values.task_title);
    formData.append("client_name", values.client_name);
    formData.append("client_address", values.client_address);
    formData.append("date", values.date);
    formData.append("start_time", values.start_time);
    formData.append("latitude", location.latitude);
    formData.append("longitude", location.longitude);
    formData.append("day", dayId);
    try {
      const response = await TaskService.createTask(formData);
      if (response.status === 201) {
        toast.success(MESSAGES.TASK_ADDED);
        navigate(HOME);
      } else {
        toast.error(MESSAGES.FAILED_SUBMISSION);
      }
    } catch (error) {
      toast.error(MESSAGES.FAILED_SUBMISSION);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    getGeoLocation();
  }, []);

  useEffect(() => {
    if (!dayId) {
      toast.error(MESSAGES.INVALID_DAY_ID);
      navigate(HOME);
    }
  }, [dayId]);

  if (loadError) return <div>{MESSAGES.MAP_LOADING_ERROR}</div>;
  if (!isLoaded || !isGeoReady)
    return (
      <div className="flex justify-center mt-4">
        <Spinner size={30} />
      </div>
    );

  return (
    <div className="max-w-3xl mx-auto rounded-lg flex justify-center items-center">
      <div className="bg-white rounded-lg p-2 w-full max-w-3xl shadow-md">
        <div className="flex justify-start gap-24 md:gap-64 mb-4 pt-2 pl-2">
          <button
            type="button"
            onClick={() => navigate(HOME)}
            className="text-primary hover:text-secondary text-2xl"
            title="Back"
          >
            <HiOutlineArrowLeft />
          </button>
          <h2 className="text-2xl font-bold text-primary text-center">
            Add Task
          </h2>
        </div>

        <Formik initialValues={initialAttendanceValue} onSubmit={handleSubmit}>
          {() => (
            <Form className="text-secondary px-2">
              <div>
                <FloatingInput label="Title" name="task_title" type="text" />
              </div>
              <div>
                <FloatingInput
                  label="Client Name"
                  name="client_name"
                  type="text"
                />
              </div>
              <div>
                <FloatingInput
                  label="Client Address"
                  name="client_address"
                  type="text"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium">Date</label>
                  <Field
                    name="date"
                    type="date"
                    disabled
                    className="w-full border border-gray-300 rounded px-3 mt-1 text-gray-500 bg-gray-100"
                  />
                  <ErrorMessage
                    name="date"
                    component="div"
                    className="text-red-600 text-sm"
                  />
                </div>
                <div>
                  <label className="block font-medium">Time</label>
                  <Field
                    name="start_time"
                    type="time"
                    disabled
                    className="w-full border border-gray-300 rounded px-3 mt-1 text-gray-500 bg-gray-100"
                  />
                  <ErrorMessage
                    name="start_time"
                    component="div"
                    className="text-red-600 text-sm"
                  />
                </div>
              </div>
              {/* GOOGLE MAP CODE START */}
              <label className="block font-medium">Current Location</label>
              {isLoaded && location.latitude && location.longitude && (
                <div className="mt-1 rounded overflow-hidden border">
                  <GoogleMap
                    onLoad={() => setIsMapReady(true)}
                    mapContainerStyle={containerStyle}
                    center={{
                      lat: parseFloat(location.latitude),
                      lng: parseFloat(location.longitude),
                    }}
                    zoom={14}
                    options={{
                      gestureHandling: "none",
                      draggable: false,
                      zoomControl: false,
                      keyboardShortcuts: false,
                      scrollwheel: false,
                    }}
                  >
                    <Marker
                      key={`${location.latitude}-${location.longitude}`} // force re-render
                      position={{
                        lat: parseFloat(location.latitude),
                        lng: parseFloat(location.longitude),
                      }}
                    />
                  </GoogleMap>
                </div>
              )}

              {/* GOOGLE MAP FINISH */}

              <div className="flex justify-between mt-8">
                {/* CSS to be done based after viewing other pages */}
                <button
                  type="button"
                  onClick={() => navigate(HOME)}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-4 py-2 text-white rounded transition ${
                    submitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary hover:bg-secondary"
                  } `}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddTask;
